export const SHARE_TARGET_UPDATE_GUARD = 'linkflare:share-target-update-pending'

// Bounds both the registration lookup and the controllerchange wait. Past this
// we boot on the bundle we already have rather than leave the share sheet
// hanging on a worker that never answers.
const UPDATE_TIMEOUT_MS = 2000

// Storage access itself throws when site data is blocked (Safari "Block All
// Cookies", a sandboxed iframe), so it must never be touched from a default
// parameter — that would reject before the body can decide it isn't needed.
// Mirrors the safeStorage() idiom in highlightShareStorage.js.
function resolveStorage(storage) {
  if (storage !== undefined) return storage
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function resolveServiceWorker(serviceWorker) {
  if (serviceWorker !== undefined) return serviceWorker
  try {
    return typeof navigator !== 'undefined' ? navigator.serviceWorker : null
  } catch {
    return null
  }
}

function readAndClearGuard(storage) {
  try {
    if (!storage?.getItem(SHARE_TARGET_UPDATE_GUARD)) return false
    storage.removeItem(SHARE_TARGET_UPDATE_GUARD)
    return true
  } catch {
    return false
  }
}

function setGuard(storage) {
  try {
    storage.setItem(SHARE_TARGET_UPDATE_GUARD, '1')
    return true
  } catch {
    // Without persistent session state the reload below could loop forever.
    return false
  }
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    Promise.resolve(promise).then(
      (value) => { clearTimeout(timer); resolve(value) },
      (error) => { clearTimeout(timer); reject(error) }
    )
  })
}

async function findWaitingWorker(container, timeoutMs) {
  try {
    // Bounded: a wedged service worker container can leave getRegistration()
    // pending forever, and this runs before the app mounts.
    const registration = await withTimeout(container.getRegistration(), timeoutMs)
    return registration?.waiting ?? null
  } catch {
    return null
  }
}

/**
 * Apply a waiting service worker before a share target boots.
 *
 * `navigateFallback` serves every navigation — `/share` and `/save` included —
 * from the precache, and the PWA registers in `prompt` mode (see
 * working-material/plans/pwa-update-prompt.md), so a client that never accepted
 * the update toast keeps running the bundle it last opted into. On most routes
 * that is the deliberate trade-off. On a share target it is not: the share
 * parser has to be current or the dialog prefills junk, and the flow prefills
 * and redirects long before the user could act on an update prompt — so the one
 * route whose correctness depends on being fresh is the one route that can never
 * self-heal.
 *
 * A share sheet lands on a cold boot with nothing in flight and no unsaved
 * edits, which makes it the one safe place to apply the waiting worker without
 * the surprise mid-action reload that `prompt` mode exists to prevent.
 *
 * Deliberately only handles an ALREADY-waiting worker: calling
 * `registration.update()` would put a network round-trip in front of every
 * share. The hourly poll in main.js plus the browser's own navigation checks
 * make `waiting` the state a stale client is actually parked in.
 *
 * Every failure path resolves false and boots normally — this runs ahead of
 * `app.mount()`, so it must never be able to take the app down with it.
 *
 * @returns {Promise<boolean>} true when a reload was triggered — the caller
 *   should stop bootstrapping rather than mount the outgoing bundle.
 */
export async function applyPendingShareTargetUpdate({
  isShareTarget,
  serviceWorker,
  storage,
  reload = () => window.location.reload(),
  timeoutMs = UPDATE_TIMEOUT_MS
} = {}) {
  if (!isShareTarget) return false

  const container = resolveServiceWorker(serviceWorker)
  if (!container) return false

  const store = resolveStorage(storage)

  // We just came back from the reload below — this IS the newest bundle.
  if (readAndClearGuard(store)) return false

  const waiting = await findWaitingWorker(container, timeoutMs)
  if (!waiting) return false

  return new Promise((resolve) => {
    let settled = false

    const settle = (updated) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      container.removeEventListener('controllerchange', onControllerChange)
      resolve(updated)
    }

    const onControllerChange = () => {
      // Write the guard only here, immediately before the reload it protects,
      // so no other path has an unwind to forget. Without somewhere to record
      // it, reloading could loop — let the activated worker stand and boot on
      // the bundle we have instead.
      if (!setGuard(store)) {
        settle(false)
        return
      }
      settle(true)
      reload()
    }

    const timer = setTimeout(() => settle(false), timeoutMs)
    container.addEventListener('controllerchange', onControllerChange)

    try {
      waiting.postMessage({ type: 'SKIP_WAITING' })
    } catch {
      settle(false)
    }
  })
}
